import type { Tool } from './tools';

type Props = {
  tool: Tool;
  accent: string;
  onLaunch: (tool: Tool) => void;
  onAbout?: () => void;
  hasAccess: boolean;
};

export function FeaturedRow({ tool, accent, onLaunch, onAbout, hasAccess }: Props) {
  const featureAccent = tool.accent ?? accent;
  const locked = !hasAccess;
  return (
    <div
      style={{
        position: 'relative',
        borderRadius: 14,
        overflow: 'hidden',
        background: 'var(--ink)',
        color: '#fff',
        border: `1.5px solid ${featureAccent}`,
        display: 'grid',
        gridTemplateColumns: '1.1fr 1.6fr',
        minHeight: 240,
      }}
    >
      <div
        style={{
          position: 'relative',
          padding: '28px 28px 24px',
          borderRight: '1px solid rgba(255,255,255,.08)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: featureAccent,
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 99,
              background: featureAccent,
              display: 'inline-block',
            }}
          />
          Core platform
        </div>
        <div style={{ fontSize: 160, fontWeight: 700, lineHeight: 0.78, letterSpacing: '-0.05em' }}>
          {tool.mono}
        </div>
        <svg
          viewBox="0 0 400 240"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            opacity: 0.6,
            pointerEvents: 'none',
          }}
          aria-hidden
          preserveAspectRatio="none"
        >
          <path
            d="M -20 200 C 100 60, 300 60, 420 200"
            fill="none"
            stroke={featureAccent}
            strokeWidth="1.2"
            opacity=".7"
          />
          <path
            d="M -20 220 C 100 100, 300 100, 420 220"
            fill="none"
            stroke={featureAccent}
            strokeWidth="1.2"
            opacity=".5"
          />
          <path
            d="M -20 240 C 100 140, 300 140, 420 240"
            fill="none"
            stroke={featureAccent}
            strokeWidth="1.2"
            opacity=".3"
          />
        </svg>
      </div>
      <div
        style={{
          padding: '28px 32px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: 16,
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 8 }}>
            <h3
              style={{
                fontSize: 38,
                fontWeight: 700,
                letterSpacing: '-0.02em',
                margin: 0,
                color: '#fff',
              }}
            >
              {tool.name}
            </h3>
            <span style={{ fontSize: 14, color: 'rgba(255,255,255,.6)' }}>{tool.tagline}</span>
          </div>
          <p
            style={{
              margin: 0,
              fontSize: 15,
              lineHeight: 1.55,
              color: 'rgba(255,255,255,.78)',
              maxWidth: 560,
            }}
          >
            {tool.desc}
          </p>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 16,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                color: '#fff',
              }}
            >
              <span className="dot live" /> Live
            </span>
            <span className="mono" style={{ color: 'rgba(255,255,255,.55)' }}>
              {tool.audience.join(' · ')}
            </span>
            <span className="mono" style={{ color: 'rgba(255,255,255,.55)' }}>
              Updated {tool.updated}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => !locked && onLaunch(tool)}
              disabled={locked}
              title={locked ? 'You don’t have a role that grants access to this tool.' : undefined}
              style={{
                border: 'none',
                cursor: locked ? 'not-allowed' : 'pointer',
                background: locked ? 'rgba(255,255,255,.18)' : featureAccent,
                color: locked ? 'rgba(255,255,255,.5)' : '#fff',
                padding: '12px 18px',
                borderRadius: 999,
                fontFamily: 'inherit',
                fontWeight: 500,
                fontSize: 14,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              {locked ? `No access to ${tool.name}` : `Launch ${tool.name}`}
              <svg width="13" height="13" viewBox="0 0 11 11">
                <path
                  d="M2 9 L9 2 M4 2 L9 2 L9 7"
                  stroke="#fff"
                  strokeWidth="1.6"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            {onAbout && (
              <button
                onClick={onAbout}
                style={{
                  border: '1px solid rgba(255,255,255,.25)',
                  cursor: 'pointer',
                  background: 'transparent',
                  color: '#fff',
                  padding: '12px 18px',
                  borderRadius: 999,
                  fontFamily: 'inherit',
                  fontWeight: 500,
                  fontSize: 14,
                }}
              >
                About {tool.name}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
